import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';
import config from 'config';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import SourceIcon from '@mui/icons-material/Source';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LinkIcon from '@mui/icons-material/Link';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import RuleIcon from '@mui/icons-material/Rule';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DvrIcon from '@mui/icons-material/Dvr';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ChurchIcon from '@mui/icons-material/Church';
const iconMap = {
    IconDashboard: DashboardIcon,
    SettingsIcon: SettingsIcon,
    RequestQuoteIcon: RequestQuoteIcon,
    AccountTreeIcon: AccountTreeIcon,
    PeopleAltIcon: PeopleAltIcon,
    DescriptionIcon: DescriptionIcon,
    ReceiptLongIcon: ReceiptLongIcon,
    RequestPageIcon: RequestPageIcon,
    SourceIcon: SourceIcon,
    FileDownloadIcon: FileDownloadIcon,
    PictureAsPdfIcon: PictureAsPdfIcon,
    LinkIcon: LinkIcon,
    PointOfSaleIcon: PointOfSaleIcon,
    DynamicFeedIcon: DynamicFeedIcon,
    RuleIcon: RuleIcon,
    CloudDownloadIcon: CloudDownloadIcon,
    CancelScheduleSendIcon: CancelScheduleSendIcon,
    AssignmentLateIcon: AssignmentLateIcon,
    ReceiptIcon: ReceiptIcon,
    DvrIcon: DvrIcon,
    NoteAddIcon: NoteAddIcon,
    ChurchIcon: ChurchIcon
};
const getIconComponent = (iconName) => {
    // Convierte el nombre del icono en un componente de icono dinámico
    return iconMap[iconName] || null;
};


const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    //const Icon = icons[item.icon];
    const IconComponent = getIconComponent(item?.icon);
    // console.log({ Icon })
    const menuIcon = IconComponent ? (
        <IconComponent stroke={1.5} size="1.3rem" />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
                height: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget = '_self';
    if (item?.target) {
        itemTarget = '_blank';
    }

    let listItemProps = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} to={`${config.basename}${item?.url}`} target={itemTarget} />)
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item?.url, target: itemTarget };
    }

    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
    };

    // active menu item on page load
    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
        // eslint-disable-next-line
    }, []);

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                borderRadius: `${customization.borderRadius}px`,
                mb: 0.5,
                alignItems: 'flex-start',
                backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                py: level > 1 ? 1 : 1.25,
                pl: `${level * 24}px`
            }}
            selected={customization.isOpen.findIndex((id) => id === item.id) > -1}
            onClick={() => itemHandler(item.id)}
        >
            <ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 36 }}>
                {menuIcon}
            </ListItemIcon>
            <ListItemText
                primary={
                    <Typography variant={customization.isOpen.findIndex((id) => id === item.id) > -1 ? 'h5' : 'body1'} color="inherit">
                        {item.title}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
